<template>
  <div class="student-document">
    <div class="student-document-header">
      <div class="student-document-meta">
        <span class="student-document-type">{{ typeLabel }}</span>
        <span>{{ isMine ? "Por mim" : studentDocument.author_name }}</span>
        <span>{{ formattedDate }}</span>
      </div>
      <button
        v-if="canDelete"
        type="button"
        class="student-document-action is-danger"
        title="Excluir postagem"
        aria-label="Excluir postagem"
        @click="$emit('delete', studentDocument.id)"
      >
        <b-icon icon="trash" aria-hidden="true"></b-icon>
      </button>
    </div>

    <p v-if="studentDocument.comment" class="student-document-comment">
      {{ studentDocument.comment }}
    </p>

    <ul class="student-document-files">
      <li
        v-for="file in studentDocument.files"
        :key="file.id"
        class="student-document-file"
      >
        <div class="student-document-icon" :class="`is-${fileExtension(file)}`">
          {{ fileExtension(file) }}
        </div>
        <a
          v-if="canPreview(file)"
          :href="file.link"
          target="_blank"
          class="student-document-name"
          title="Abrir arquivo"
        >
          {{ fileNameWithoutExtension(file) }}
        </a>
        <a
          v-else
          :href="file.urlDownload"
          class="student-document-name"
          title="Baixar arquivo"
        >
          {{ fileNameWithoutExtension(file) }}
        </a>
        <a
          :href="file.urlDownload"
          class="student-document-action"
          title="Baixar arquivo"
          aria-label="Baixar arquivo"
        >
          <b-icon icon="download" aria-hidden="true"></b-icon>
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
import moment from "moment";

// Browsers only render PDFs inline; other files are downloaded instead.
const PREVIEWABLE_EXTENSIONS = ["pdf"];

export default {
  name: "StudentDocumentItem",
  props: {
    studentDocument: {
      type: Object,
      required: true,
    },
    typeLabel: {
      type: String,
      required: true,
    },
    isMine: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    formattedDate() {
      return moment(this.studentDocument.created_at).format(
        "DD/MM/YYYY [às] HH:mm"
      );
    },
  },
  methods: {
    canPreview(file) {
      return PREVIEWABLE_EXTENSIONS.includes(this.fileExtension(file));
    },
    fileExtension(file) {
      const fileName = file.name || "";
      return fileName.split(".").pop().toLowerCase();
    },
    fileNameWithoutExtension(file) {
      const fileName = file.name || "";
      const extensionStart = fileName.lastIndexOf(".");
      if (extensionStart <= 0) {
        return fileName;
      }
      return fileName.slice(0, extensionStart);
    },
  },
};
</script>

<style lang="scss" scoped>
.student-document {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
  border-bottom: 1px solid #e6e6e6;
}

.student-document-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.student-document-files {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
}

.student-document-file {
  display: flex;
  align-items: center;
  gap: 16px;
}

.student-document-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #ececec;
  color: #666666;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;

  &.is-pdf {
    background: #fde8e8;
    color: #c62828;
  }

  &.is-docx {
    background: #e3ecfb;
    color: #2b5797;
  }
}

.student-document-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: #1a1a1a;
    text-decoration: underline;
  }
}

.student-document-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #666666;
  font-size: 14px;

  span + span::before {
    content: "•";
    margin-right: 8px;
  }
}

.student-document-type {
  color: #b23066;
  font-weight: 600;
}

.student-document-comment {
  margin: 0;
  color: #333;
  font-size: 15px;
  white-space: pre-line;
}

.student-document-action {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #666666;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba($green1, 0.12);
    color: $green1;
  }

  &.is-danger:hover {
    background: rgba($red, 0.12);
    color: $red;
  }
}
</style>
